import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { SessionDeleteDialog } from '@/components/admin/sessions/session-delete-dialog';
import { ReplayPanel } from '@/components/admin/sessions/walk/replay-panel';
import { SessionHead } from '@/components/admin/sessions/walk/session-head';
import {
  SessionReading,
  type SessionReadingTab,
} from '@/components/admin/sessions/walk/session-reading';
import { SessionTiles } from '@/components/admin/sessions/walk/session-tiles';
import { TrashedBanner } from '@/components/admin/sessions/walk/trashed-banner';
import { WalkNoyauTrial } from '@/components/admin/sessions/walk/walk-noyau-trial';
import { WalkRangementTrial } from '@/components/admin/sessions/walk/walk-rangement-trial';
import { VitrineTrialsNote } from '@/components/admin/vitrine/vitrine-trials-note';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError } from '@/lib/api';
import {
  SESSION_DETAIL_KEY,
  fetchSessionDetail,
  restoreSession,
  trashSession,
} from '@/lib/session-api';
import { useIsVitrine } from '@/lib/use-instance';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * `/admin/sessions/<id>` - the PARCOURS of a session (§12.2).
 *
 * Opening a session opens its walk, page by page, in the order it traversed
 * them. That replaced the three-panel reader the spec used to describe, and the
 * grill of 2026-09-14 gives the reason in one sentence: a session is a timeline
 * of PAGES of which the protocol is only one, and a screen that opened the
 * conversation alone left a session without a turn on an empty page and showed
 * the values somebody typed far from where they typed them.
 *
 * ## One query for two screens
 *
 * `fetchSessionDetail` serves the parcours AND the conversation of §12.3, and
 * they share this key on purpose: a conversation opened from here must not
 * re-fetch what this page already holds. What the reading never carries is the
 * prompts served to the seats - they have their own route (§8.7), read on a
 * click by the screen that opens them.
 *
 * ## A trashed session opens, and reads
 *
 * §12.5: « une session en corbeille reste ouvrable en lecture sous un bandeau
 * qui donne sa date d'effacement ». So a 404 here is a session that never
 * existed, never one that was deleted - and the two get two sentences, because
 * there are two different things to do about them.
 *
 * ## Two tabs under the eight figures (#172)
 *
 * The head and the tiles are common; under them, Parcours and Variables
 * (`SessionReading`). The tab travels in the URL so a pasted link opens the
 * right one, and Parcours - the default - writes none.
 */

/** How often a session whose rangement is still running is read again. */
const RANGEMENT_POLL_MS = 3000;

/** What the screen carries in the address bar: only « variables » is ever written. */
type SessionWalkSearch = {
  tab?: 'variables' | undefined;
};

/**
 * `?tab=` as the router hands it over. `validateSearch` NEVER throws: an
 * unknown tab falls back on Parcours, never on a blank screen.
 */
export function readSearch(search: Record<string, unknown>): SessionWalkSearch {
  return search.tab === 'variables' ? { tab: 'variables' } : {};
}

/** The search a tab writes: none for Parcours, the default. */
export function searchOfTab(tab: SessionReadingTab): SessionWalkSearch {
  return tab === 'variables' ? { tab: 'variables' } : {};
}

export const Route = createFileRoute('/admin/sessions/$sessionId')({
  validateSearch: readSearch,
  component: SessionWalkPage,
});

function SessionWalkPage() {
  const { sessionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  // The vitrine makes no paid call: the two dry trials give way to a note that
  // sends the operator to form.dev, on the session once it has gone up
  // (docs/specs/vitrine.md section 7, H8).
  const vitrine = useIsVitrine();

  const detail = useQuery({
    queryKey: SESSION_DETAIL_KEY(email, sessionId),
    queryFn: () => fetchSessionDetail(sessionId),
    retry: false,
    // The ranger of a session still running (scenario-succes.md §7.2, « en cours »)
    // settles by itself in the background: the page reads again until it has.
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.rangement?.state === 'pending'
        ? RANGEMENT_POLL_MS
        : false,
  });

  const rejected = useRejectOn401(detail.error);
  const missing = detail.error instanceof ApiError && detail.error.status === 404;

  /*
    Both gestures re-read rather than patching the cache: the trash writes a
    stamp the server owns (`deletedAt`), and the banner prints it. A local
    `new Date()` would put a date on screen that the next reload contradicts.

    TWO keys are invalidated, and the second is not tidiness. « Afficher la
    corbeille » SWAPS the list (§12.4), so a session trashed from here leaves
    one list and joins the other: a list left alone would keep showing a row
    that is no longer in it, on the screen the breadcrumb goes back to.
  */
  const reread = (): void => {
    void queryClient.invalidateQueries({ queryKey: SESSION_DETAIL_KEY(email, sessionId) });
    void queryClient.invalidateQueries({ queryKey: ['sessions', email] });
  };

  const trash = useMutation({
    mutationFn: () => trashSession(sessionId),
    onSuccess: () => {
      setConfirming(false);
      reread();
    },
  });

  const restore = useMutation({
    mutationFn: () => restoreSession(sessionId),
    onSuccess: reread,
  });

  const tab: SessionReadingTab = search.tab === 'variables' ? 'variables' : 'parcours';
  const selectTab = (next: SessionReadingTab): void => {
    void navigate({
      to: '/admin/sessions/$sessionId',
      params: { sessionId },
      search: searchOfTab(next),
      replace: true,
    });
  };

  return (
    <section data-testid={SESSIONS_TEST_IDS.walk} className="flex flex-col gap-5">
      {detail.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement de la session" />
        </div>
      ) : null}

      {missing ? <p className="text-gray-700">Aucune session sous cet identifiant.</p> : null}
      {detail.isError && !missing && !rejected ? (
        <p role="alert" className="text-gray-700">
          Cette session n’a pas pu être lue. Rechargez la page.
        </p>
      ) : null}

      {detail.data ? (
        <>
          {detail.data.session.deletedAt === null ? null : (
            <TrashedBanner
              deletedAt={detail.data.session.deletedAt}
              busy={restore.isPending}
              onRestore={() => restore.mutate()}
            />
          )}

          <SessionHead detail={detail.data} onDelete={() => setConfirming(true)} />
          {/*
            « Rejeu par Claude » (rejeu.md §12, #193): under the head and BEFORE
            the figures, on a session Claude played in the place of a person.
          */}
          {detail.data.replay === undefined || detail.data.replay === null ? null : (
            <ReplayPanel sessionId={detail.data.session.id} replay={detail.data.replay} />
          )}
          <SessionTiles detail={detail.data} />

          <SessionReading
            detail={detail.data}
            tab={tab}
            onSelectTab={selectTab}
            trial={
              /*
                The trial of the analyst (noyau.md §9.4, #164) on every session
                that played a protocol: it reads that protocol's development
                reading. A section of its own and not a block of the walk - the
                fil says what the session DID, and a trial is something the
                operator does to it.
              */
              detail.data.cascade.protocol === null ? null : vitrine ? (
                <VitrineTrialsNote />
              ) : (
                <>
                  <h2 className="font-semibold text-gray-600 text-micro uppercase tracking-wide">
                    Lecture à blanc
                  </h2>
                  <WalkNoyauTrial
                    sessionId={detail.data.session.id}
                    protocolId={detail.data.cascade.protocol.id}
                    email={email}
                  />
                  {/*
                    The same for the ranger (scenario-succes.md §7.3, #177): its
                    consigne is the development version's Rangement section, and
                    the column it fills is drawn under its own block.
                  */}
                  <h2 className="font-semibold text-gray-600 text-micro uppercase tracking-wide">
                    Rangement à blanc
                  </h2>
                  <WalkRangementTrial
                    detail={detail.data}
                    protocolId={detail.data.cascade.protocol.id}
                  />
                </>
              )
            }
          />
        </>
      ) : null}

      {/*
        La MÊME boîte que la liste, montée et non recopiée. Une copie a divergé
        sur quatre points - le titre, le libellé de confirmation, un ton
        `destructive` pour un geste que « Restaurer » défait pendant trente
        jours, et une phrase de purge qui oubliait les variables là où §12.5 en
        exige trois : les tours, les variables, les entrées servies.
      */}
      {confirming && detail.data ? (
        <SessionDeleteDialog
          row={detail.data.session}
          busy={trash.isPending}
          error={trash.isError ? 'La suppression a échoué. Réessayez.' : null}
          onConfirm={() => trash.mutate()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </section>
  );
}
